import * as React from 'react';
import { memo, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import {
  ArrowLeftOnRectangleIcon,
  ArrowUpRightIcon,
  Cog6ToothIcon,
  DocumentIcon,
  MapIcon,
  XMarkIcon,
  DocumentArrowUpIcon,
  SparklesIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import Cookies from 'js-cookie';
import { useIntercom } from 'react-use-intercom';

import { cn, config, HeadphoneAltIcon } from '@restworld/utils-common';
import { useNavigationContext } from '@restworld/utils-common';
import {
  Badge,
  IconButton,
  Link,
  Logo,
  Select,
  renderFlag,
  IconComponentType,
  Button,
  Heading,
} from '@restworld/ui-ds-base';
import { useAuth } from '@restworld/feature-authentication';

import { NavMenuItem, LinkBase } from '../../..';

function NavMenuContent({
  onCloseMenu,
  avatarImageURL,
  workerInfo,
  onStartLogin,
  onShowUploadCvModal,
}: NavMenuContentProps) {
  const { t } = useTranslation('common');
  const { router } = useNavigationContext();
  const locale = useCurrentLocale();
  const { user } = useAuth();
  const languageOptionsList = useLanguageOptionsList();
  const { switchLocale } = useLocaleSwitch();
  const { primaryNavItems, primaryNavItemsTitle, secondaryNavItems, secondaryNavItemsTitle, tertiaryNavItems } =
    useNavItemElements({
      avatarImageURL,
      workerInfo,
      onShowUploadCvModal,
      onCloseMenu,
    });
  const { isEmployer, isAmbassador } = useUserType();

  const localeSelectValue = useMemo(() => {
    const currentOption = languageOptionsList.find((v) => v.key === locale);
    if (currentOption) return { ...currentOption, value: currentOption.key };
    return { label: '', value: '' };
  }, [languageOptionsList, locale]);

  const actionBtnText = useMemo(
    () => t(isEmployer ? 'return_to_personal_area' : isAmbassador ? 'return_to_dashboard' : 'log_in'),
    [isAmbassador, isEmployer, t]
  );

  const onClickActionBtn = useCallback(() => {
    if (isEmployer) router.push('/v2/employer/homepage');
    else if (isAmbassador) router.push('/challenge-wom/dashboard');
    else if (onStartLogin) onStartLogin();
  }, [isAmbassador, isEmployer, onStartLogin, router]);

  return (
    <div className="h-[100%] max-h-[100svh] overflow-auto w-full pb-6 pt-3 px-4 flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between mb-10">
          <LinkBase link="/" newTab={false}>
            <Logo variant="mark" title="restworld" className="h-8 w-auto text-purple-700" />
          </LinkBase>
          <IconButton ariaLabel="close" Icon={XMarkIcon} onClick={onCloseMenu} />
        </div>
        <div className="pb-5">
          {!!primaryNavItemsTitle && <ItemGroupTitle content={primaryNavItemsTitle} />}
          <ItemsWrapper>{primaryNavItems}</ItemsWrapper>
        </div>
        <SectionWrapper hasTopBorder={!!secondaryNavItems?.length} className="pt-5">
          {!!secondaryNavItemsTitle && <ItemGroupTitle content={secondaryNavItemsTitle} />}
          <ItemsWrapper>{secondaryNavItems}</ItemsWrapper>
        </SectionWrapper>
      </div>
      <SectionWrapper hasTopBorder={!!tertiaryNavItems?.length}>
        <div className="py-6">
          <ItemsWrapper>{tertiaryNavItems}</ItemsWrapper>
        </div>
        <div className="flex flex-col gap-3">
          {(!user?.id || isEmployer || isAmbassador) && (
            <Button
              fullwidth
              onClick={onClickActionBtn}
              StartIcon={isEmployer || isAmbassador ? ArrowUpRightIcon : undefined}
            >
              {actionBtnText}
            </Button>
          )}
          <Select
            fullwidth
            options={languageOptionsList.map((v) => ({ ...v, value: v.key }))}
            onChange={(v) => switchLocale(v.value)}
            value={localeSelectValue}
            isContentCentered
          />
        </div>
      </SectionWrapper>
    </div>
  );
}
export default memo(NavMenuContent);

enum RouteKey {
  PROFILE = 'profilo',
  JOB_EXPLORER = 'cerco-lavoro',
  MY_APPLICATIONS = 'candidature',
  FIND_ACCOMODATION = 'find-accomodation',
  LEARN = 'learn',
  GET_SUPPORT = 'get-support',
  SETTINGS = 'impostazioni',
  LOGOUT = 'logout',
  UPLOAD_CV = 'upload-cv',
  CREATE_CV = 'candidati',
  HIRE_WITH_RW = 'hire-with-rw',
  POST_VENUE = 'post-venue',
  REFER_VENUE = 'porta-un-locale',
}

function useNavItems({ avatarImageURL, workerInfo, onShowUploadCvModal }: IUseNavItems) {
  const { t } = useTranslation('common');
  const { show: showIntercom } = useIntercom();
  const { logout } = useAuth();
  const isProfileComplete = workerInfo?.isProfileComplete;
  const { isEmployer, isAmbassador } = useUserType();

  const navItems: NavItem[] = useMemo(
    () => [
      {
        title: (
          <span className="block">
            <Heading
              as="span"
              size={5}
              weight="medium"
              color="neutral"
              // block so the "Complete profile" warning Badge drops to its own line below the
              // label (matches master's block Title); as an inline span it sat on the same line.
              className={isProfileComplete ? 'block' : 'block mb-2'}
            >
              {t('my_cv')}
            </Heading>
            {!isProfileComplete && (
              <Badge variant="warning">
                <span className="flex items-center gap-1">
                  <ExclamationTriangleIcon className="h-[16px] w-[16px" /> <span>{t('complete_profile')}</span>
                </span>
              </Badge>
            )}
          </span>
        ),
        group: 'primary',
        type: 'default',
        Icon: avatarImageURL ? undefined : UserCircleIcon,
        imageUrl: avatarImageURL,
        href: '/profilo',
        routeKey: RouteKey.PROFILE,
      },
      {
        title: t(isEmployer || isAmbassador ? 'explore_map' : 'search_for_jobs'),
        group: 'primary',
        type: 'default',
        Icon: MapIcon,
        href: '/cerco-lavoro',
        routeKey: RouteKey.JOB_EXPLORER,
      },
      {
        title: t('my_applications'),
        group: 'primary',
        type: 'default',
        Icon: DocumentIcon,
        indicator: workerInfo?.activeApplicationsCount || undefined,
        href: '/candidature',
        routeKey: RouteKey.MY_APPLICATIONS,
      },
      {
        title: t('refer_a_venue'),
        group: 'secondary',
        type: 'default',
        Icon: BuildingOfficeIcon,
        href: '/porta-un-locale',
        routeKey: RouteKey.REFER_VENUE,
      },
      {
        title: t('find_accommodation_with_partners'),
        group: 'secondary',
        type: 'externalLink',
        Icon: ArrowUpRightIcon,
        href: config.accomodationPartnerUrl,
        routeKey: RouteKey.FIND_ACCOMODATION,
      },
      {
        title: t('learn_with_partners'),
        group: 'secondary',
        type: 'externalLink',
        Icon: ArrowUpRightIcon,
        href: config.learningPartnerUrl,
        routeKey: RouteKey.LEARN,
      },
      {
        title: t('support'),
        group: 'tertiary',
        type: 'default',
        Icon: HeadphoneAltIcon,
        onClick: showIntercom,
        routeKey: RouteKey.GET_SUPPORT,
      },
      {
        title: t('settings'),
        group: 'tertiary',
        type: 'default',
        Icon: Cog6ToothIcon,
        href: '/impostazioni',
        routeKey: RouteKey.SETTINGS,
      },
      {
        title: t('log_out'),
        group: 'tertiary',
        type: 'default',
        Icon: ArrowLeftOnRectangleIcon,
        onClick: logout,
        routeKey: RouteKey.LOGOUT,
      },
      {
        title: t('upload_cv'),
        group: 'primary',
        type: 'default',
        Icon: DocumentArrowUpIcon,
        routeKey: RouteKey.UPLOAD_CV,
        onClick: onShowUploadCvModal,
      },
      {
        title: (
          <>
            <span>{t('create_cv')}</span> <RecommendedBadge />
          </>
        ),
        group: 'primary',
        type: 'default',
        Icon: SparklesIcon,
        href: '/candidati',
        routeKey: RouteKey.CREATE_CV,
      },
      {
        title: t('hire_with_restworld'),
        group: 'secondary',
        type: 'default',
        Icon: UserGroupIcon,
        href: `${config.hireWithRestworldUrl}?utm_source=worker-app`,
        routeKey: RouteKey.HIRE_WITH_RW,
      },
      // {
      //   title: t('post_your_venue_for_free'),
      //   group: 'secondary',
      //   type: 'default',
      //   Icon: BuildingOfficeIcon,
      //   href: '/getting-started?utm_source=worker-app',
      //   routeKey: RouteKey.POST_VENUE,
      // },
    ],
    [
      t,
      isProfileComplete,
      avatarImageURL,
      isEmployer,
      isAmbassador,
      workerInfo?.activeApplicationsCount,
      showIntercom,
      logout,
      onShowUploadCvModal,
    ]
  );

  return { navItems };
}

const workerRoutes: RouteKey[] = [
  RouteKey.PROFILE,
  RouteKey.JOB_EXPLORER,
  RouteKey.MY_APPLICATIONS,
  RouteKey.REFER_VENUE,
  RouteKey.FIND_ACCOMODATION,
  RouteKey.LEARN,
  RouteKey.GET_SUPPORT,
  RouteKey.SETTINGS,
  RouteKey.LOGOUT,
];
const loggedOutUserRoutes: RouteKey[] = [
  RouteKey.JOB_EXPLORER,
  RouteKey.UPLOAD_CV,
  RouteKey.CREATE_CV,
  RouteKey.HIRE_WITH_RW,
  RouteKey.POST_VENUE,
];
const leadUserRoutes: RouteKey[] = [...loggedOutUserRoutes, RouteKey.LOGOUT];
const employerOrAmbassadorRoutes: RouteKey[] = [RouteKey.JOB_EXPLORER, RouteKey.LOGOUT];

function useNavItemElements({ avatarImageURL, workerInfo, onShowUploadCvModal, onCloseMenu }: IUseNavItems = {}) {
  const { t } = useTranslation('common');
  const { navItems } = useNavItems({ avatarImageURL, workerInfo, onShowUploadCvModal });
  const { isEmployer, isLeadUser, isLoggedOut, isWorker, isAmbassador } = useUserType();
  const { renderNavItems } = useRenderNavItems(onCloseMenu);

  const primaryNavItems = useMemo(() => {
    const filteredItems = navItems.filter((item) => item.group === 'primary');

    if (isWorker) return renderNavItems(filteredItems.filter((item) => workerRoutes.includes(item.routeKey)));

    if (isEmployer || isAmbassador)
      return renderNavItems(filteredItems.filter((item) => employerOrAmbassadorRoutes.includes(item.routeKey)));

    return renderNavItems(filteredItems.filter((item) => loggedOutUserRoutes.includes(item.routeKey)));
  }, [isAmbassador, isEmployer, isWorker, navItems, renderNavItems]);

  const primaryNavItemsTitle = useMemo(
    () => (isLoggedOut || isLeadUser ? t('looking_for_a_job') : ''),
    [isLeadUser, isLoggedOut, t]
  );

  const secondaryNavItems = useMemo(() => {
    const filteredItems = navItems.filter((item) => item.group === 'secondary');

    if (isWorker) return renderNavItems(filteredItems.filter((item) => workerRoutes.includes(item.routeKey)));

    if (isEmployer || isAmbassador) return null;

    return renderNavItems(filteredItems.filter((item) => loggedOutUserRoutes.includes(item.routeKey)));
  }, [isAmbassador, isEmployer, isWorker, navItems, renderNavItems]);

  const secondaryNavItemsTitle = useMemo(
    () => (isLoggedOut || isLeadUser ? t('looking_for_staff') : ''),
    [isLeadUser, isLoggedOut, t]
  );

  const tertiaryNavItems = useMemo(() => {
    const filteredItems = navItems.filter((item) => item.group === 'tertiary');

    if (isWorker) return renderNavItems(filteredItems);

    if (isLeadUser) return renderNavItems(filteredItems.filter((item) => leadUserRoutes.includes(item.routeKey)));

    if (isEmployer || isAmbassador)
      return renderNavItems(filteredItems.filter((item) => employerOrAmbassadorRoutes.includes(item.routeKey)));

    return null;
  }, [isAmbassador, isEmployer, isLeadUser, isWorker, navItems, renderNavItems]);

  return { primaryNavItems, primaryNavItemsTitle, secondaryNavItems, secondaryNavItemsTitle, tertiaryNavItems };
}

function useRenderNavItems(onCloseMenu?: () => void) {
  const { router } = useNavigationContext();

  const renderNavItems = useCallback(
    (items: NavItem[]) => {
      return items.map(({ title, type, Icon, href, routeKey, onClick, ...rest }) => {
        return type === 'externalLink' ? (
          <Link key={routeKey} variant="restworld" StartIcon={Icon} href={href} onClick={onCloseMenu}>
            {title}
          </Link>
        ) : (
          <NavMenuItem
            key={routeKey}
            isOpen
            Icon={Icon}
            href={href}
            isSelected={router?.pathname.includes(routeKey)}
            noV2Prefix
            onClick={() => {
              onClick?.();
              if (routeKey !== RouteKey.UPLOAD_CV) onCloseMenu?.();
            }}
            {...rest}
          >
            {title}
          </NavMenuItem>
        );
      });
    },
    [onCloseMenu, router?.pathname]
  );

  return { renderNavItems };
}

function useUserType() {
  const { user } = useAuth();

  const isLoggedOut = useMemo(() => !user?.id, [user?.id]);
  const isWorker = useMemo(() => user?.type === 'worker', [user?.type]);
  const isLeadUser = useMemo(() => user?.type === 'lead', [user?.type]);
  const isEmployer = useMemo(() => user?.type === 'employer' || user?.type === 'employer_contact', [user?.type]);
  // Follows the ambassador record, not the type: this is what puts "torna alla
  // dashboard" in the menu, and somebody who is both a worker and an ambassador
  // has type 'worker' (so they can still apply to job positions). Gating on the
  // type left them with no route to their dashboard except typing the URL.
  // The nav item lists are unaffected: `isWorker` is checked first below.
  const isAmbassador = useMemo(() => !!user?.['ambassador?'], [user]);

  return { isWorker, isLeadUser, isEmployer, isAmbassador, isLoggedOut };
}

const LANGUAGE_OPTIONS = {
  EN: 'en',
  IT: 'it',
} as const;
const DEFAULT_LANGUAGE = LANGUAGE_OPTIONS.IT;

export function useLanguageOptionsList() {
  const { t } = useTranslation('common');

  const languageOptions = useMemo(
    () => [
      { id: LANGUAGE_OPTIONS.IT, key: LANGUAGE_OPTIONS.IT, label: t('italian_lang_key'), icon: renderFlag('IT') },
      { id: LANGUAGE_OPTIONS.EN, key: LANGUAGE_OPTIONS.EN, label: t('english_lang_key'), icon: renderFlag('GB') },
    ],
    [t]
  );

  return languageOptions;
}

export function useCurrentLocale() {
  const { router } = useNavigationContext();
  const locale: string = router.locale || DEFAULT_LANGUAGE;

  return locale;
}

export function useLocaleSwitch() {
  const { router } = useNavigationContext();
  const basePath = router.asPath;

  const switchLocale = useCallback(
    (language: string) => {
      Cookies.set('NEXT_LOCALE', language, { expires: 365 * 24 * 60 * 60 });
      router.push(basePath, basePath, { locale: language });
    },
    [basePath, router]
  );

  return { switchLocale };
}

function ItemGroupTitle({ content }: { content: string }) {
  return (
    <Heading as="span" size={5} weight="semibold" color="neutral" className="mb-5">
      {content}
    </Heading>
  );
}

function RecommendedBadge() {
  const { t } = useTranslation('common');

  return <Badge variant="fresh">{t('recommended')}</Badge>;
}

export type NavWorkerInfo = {
  activeApplicationsCount?: number;
  isProfileComplete?: boolean;
};
interface NavMenuContentProps {
  onCloseMenu?: () => void;
  avatarImageURL?: string;
  workerInfo?: NavWorkerInfo;
  onStartSignup?: () => void;
  onStartLogin?: () => void;
  onShowUploadCvModal?: () => void;
}
interface IUseNavItems {
  avatarImageURL?: string;
  workerInfo?: NavWorkerInfo;
  onShowUploadCvModal?: () => void;
  onCloseMenu?: () => void;
}
type NavItemGroupType = 'primary' | 'secondary' | 'tertiary';
type NavItemVariantType = 'default' | 'externalLink';
interface NavItem {
  title: ReactNode;
  type: NavItemVariantType;
  href?: string;
  key?: RouteKey;
  Icon?: IconComponentType;
  imageUrl?: string;
  onClick?: () => void;
  group: NavItemGroupType;
  indicator?: number;
  routeKey: RouteKey;
}

const ItemsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-3', className)} {...props} />
);
const SectionWrapper = ({ hasTopBorder, className, ...props }: any) => (
  <div className={cn(hasTopBorder ? 'border-t border-neutral-40' : '', className)} {...props} />
);
